"use client";

import { ArrowUpRight, ExternalLink } from "lucide-react";
import { useEffect, useRef } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const latestImage = {
  title: "M101 — Solstice Salvage",
  date: "JUNE 12, 2026",
  gear: "SHARPSTAR 61EDPH III · ASI2600MC PRO",
  integration: "1.6 HRS",
  description: "Face-on grand design spiral galaxy ~21 million light-years away in Ursa Major, captured in a brief solstice window from Bortle 5 skies in Vernon, BC.",
  image: "https://cdn.astrobin.com/thumbs/nyuSVcbiiuvz_1824x0_Z-BERGNL.jpg?v=1781379108",
  astrobinUrl: "https://www.astrobin.com/duxqcz/"
};
function ObserverNotes() {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const script = document.createElement("script");
    script.src = "https://giscus.app/client.js";
    script.setAttribute("data-repo", "dolphyking/SolObservatory");
    script.setAttribute("data-repo-id", "R_kgDOS2isBQ");
    script.setAttribute("data-category", "Observer Notes");
    script.setAttribute("data-category-id", "DIC_kwDOS2isBc4C-5Dp");
    script.setAttribute("data-mapping", "pathname");
    script.setAttribute("data-strict", "0");
    script.setAttribute("data-reactions-enabled", "1");
    script.setAttribute("data-emit-metadata", "0");
    script.setAttribute("data-input-position", "top");
    script.setAttribute("data-theme", "transparent_dark");
    script.setAttribute("data-lang", "en");
    script.setAttribute("data-loading", "lazy");
    script.setAttribute("crossorigin", "anonymous");
    script.async = true;
    ref.current.appendChild(script);
    return () => {
      if (ref.current) ref.current.innerHTML = "";
    };
  }, []);
  return;
}
export default function LatestCapture() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <>
      <section id="latest-capture" className="bg-background text-foreground w-full py-24 lg:py-32 border-t border-border/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Header */}
          <div className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-4">
              <div className="w-8 h-px bg-foreground/20" />
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                LATEST CAPTURE
              </span>
            </div>
            <a href={latestImage.astrobinUrl} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors duration-300">
              <span>FULL ARCHIVE ON ASTROBIN</span>
              <ExternalLink className="size-3 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>

          {/* Card */}
          <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-0 border border-border/40 rounded-md overflow-hidden transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {/* Image */}
            <div className="lg:col-span-7 relative aspect-[4/3] lg:aspect-auto overflow-hidden bg-card">
              <img src={latestImage.image} alt={latestImage.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              <div className="absolute inset-0 pointer-events-none" style={{
              background: "linear-gradient(to right, transparent 60%, hsl(240 10% 4%) 100%)"
            }} />
            </div>

            {/* Info */}
            <div className="lg:col-span-5 flex flex-col justify-between p-8 lg:p-12 bg-card/60 backdrop-blur-sm">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="size-1.5 bg-secondary animate-pulse" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-secondary">
                    MOST RECENT
                  </span>
                </div>

                <h2 className="font-default text-3xl lg:text-4xl xl:text-5xl font-bold tracking-tighter leading-[1.05] text-foreground mb-4">
                  {latestImage.title}
                </h2>

                <p className="font-default text-[15px] leading-relaxed text-muted-foreground mb-8">
                  {latestImage.description}
                </p>
              </div>

              <div>
                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 mb-8 pt-6 border-t border-border/40">
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/60 block mb-1">DATE</span>
                    <span className="font-mono text-xs text-foreground/80">{latestImage.date}</span>
                  </div>
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/60 block mb-1">INTEGRATION</span>
                    <span className="font-mono text-xs text-foreground/80">{latestImage.integration}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/60 block mb-1">GEAR</span>
                    <span className="font-mono text-xs text-foreground/80">{latestImage.gear}</span>
                  </div>
                </div>

                {/* CTA */}
                <a href={latestImage.astrobinUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] transition-all duration-300 group/link" style={{
                color: "var(--chart-2)"
              }}>
                  <span className="border-b border-transparent group-hover/link:border-current pb-1 transition-all">
                    VIEW ON ASTROBIN
                  </span>
                  <ArrowUpRight className="size-3.5 transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ObserverNotes />
    </>;
}